import {
  ArrowLeftIcon,
  CoinsIcon,
  ShoppingBagIcon,
  TrendingUpIcon,
  XIcon,
} from "lucide-react";

import { Column } from "../Column";
import { Row } from "../Row";
import { StafferPetIncentiveToken } from "./StafferPetIncentiveToken";
import { Text } from "../Text";
import { Button } from "../ui/button";
import {
  DialogClose,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

import { dates } from "../../lib/dates";
import { cn } from "../../lib/utils";
import type { PetTransaction } from "../../types";

export const StafferPetTransactionsPage = ({
  coinBalance,
  coinLedger,
  onBack,
}: {
  coinBalance: number;
  // Newest first, straight from the API.
  coinLedger: PetTransaction[];
  onBack: () => void;
}) => {
  /* Helpers */
  // Token redemptions ride in the purchases bucket: both are things cashed in.
  const purchases = coinLedger.filter(
    (transaction) => transaction.type !== "INCOME",
  );
  const income = coinLedger.filter(
    (transaction) => transaction.type === "INCOME",
  );
  const totalSpent = purchases.reduce(
    (total, transaction) => total + Math.abs(transaction.amount),
    0,
  );
  const totalIncome = income.reduce(
    (total, transaction) => total + transaction.amount,
    0,
  );

  /* Render */
  const renderBucket = (
    title: string,
    subtitle: string,
    transactions: PetTransaction[],
    type: "INCOME" | "PURCHASE",
  ) => {
    const Icon = type === "INCOME" ? TrendingUpIcon : ShoppingBagIcon;

    return (
      <Column className="min-h-0 gap-3 border-4 border-slate-900 bg-white p-4 shadow-[4px_4px_0_#172033]">
        <Row className="items-center justify-between gap-3 border-b-2 border-dashed border-slate-300 pb-3">
          <Row className="items-center gap-2">
            <Icon
              className={cn(
                "h-5 w-5",
                type === "INCOME" ? "text-emerald-700" : "text-orange-600",
              )}
            />
            <Column className="gap-0">
              <Text className="font-mono text-xs font-black uppercase tracking-[0.12em] text-slate-900">
                {title}
              </Text>
              <Text className="font-mono text-[9px] font-bold uppercase tracking-[0.08em] text-slate-500">
                {subtitle}
              </Text>
            </Column>
          </Row>
          <Text className="font-mono text-xs font-black text-slate-500">
            {transactions.length.toString()}
          </Text>
        </Row>
        <Column className="min-h-0 gap-2 overflow-y-auto pr-1">
          {transactions.length === 0 ? (
            <Text className="py-8 text-center font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
              Nothing here yet
            </Text>
          ) : null}
          {transactions.map((transaction) => (
            <TransactionEntry key={transaction.id} transaction={transaction} />
          ))}
        </Column>
      </Column>
    );
  };

  const render = () => {
    return (
      <>
        <DialogHeader className="border-b-4 border-slate-900 pb-3 text-left">
          <Row className="w-full items-center justify-between gap-4">
            <Column className="gap-0">
              <DialogTitle className="font-mono text-xl font-black uppercase tracking-[0.1em] text-slate-900">
                Treat Transactions
              </DialogTitle>
              <DialogDescription className="font-mono text-xs uppercase tracking-[0.18em] text-orange-600">
                Everything you&apos;ve earned and collected
              </DialogDescription>
            </Column>
            <DialogClose asChild>
              <Button
                type="button"
                variant="ghost"
                className="h-8 w-8 shrink-0 p-0 text-slate-900 hover:bg-orange-100"
              >
                <XIcon />
                <Text className="sr-only">Close</Text>
              </Button>
            </DialogClose>
          </Row>
        </DialogHeader>

        <Column className="min-h-0 gap-4">
          <div className="grid grid-cols-3 gap-3">
            <TransactionSummary label="Balance" value={coinBalance} />
            <TransactionSummary label="All income" value={totalIncome} />
            <TransactionSummary label="All purchases" value={totalSpent} />
          </div>
          <div className="grid min-h-0 flex-1 grid-cols-2 gap-4">
            {renderBucket("Income", "Your earning history", income, "INCOME")}
            {renderBucket("Purchases", "Kept forever", purchases, "PURCHASE")}
          </div>
        </Column>

        <Row className="border-t-4 border-slate-900 pt-4">
          <Button
            type="button"
            variant="outlineDark"
            onClick={onBack}
            className="h-12 border-2 border-slate-900 bg-white px-5 font-mono text-xs font-black uppercase tracking-[0.12em] text-slate-900 shadow-[3px_3px_0_#172033] hover:bg-orange-50"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to yard
          </Button>
        </Row>
      </>
    );
  };

  return render();
};

const TransactionSummary = ({
  label,
  value,
}: {
  label: string;
  value: number;
}) => {
  /* Render */
  const render = () => {
    return (
      <Row className="items-center gap-3 border-2 border-slate-900 bg-amber-100 px-4 py-3 shadow-[3px_3px_0_#172033]">
        <CoinsIcon className="h-5 w-5 text-amber-600" />
        <Column className="gap-0">
          <Text className="font-mono text-[9px] font-black uppercase tracking-[0.12em] text-slate-500">
            {label}
          </Text>
          <Text className="font-mono text-lg font-black text-slate-900">
            {value.toLocaleString("en-US")}
          </Text>
        </Column>
      </Row>
    );
  };

  return render();
};

const TransactionEntry = ({ transaction }: { transaction: PetTransaction }) => {
  /* Helpers */
  const isIncome = transaction.type === "INCOME";
  const sign = isIncome ? "+" : "−";

  /* Render */
  // A redemption spends a token, not coins, so it wears the token instead of
  // an amount.
  const renderAmount = () => {
    if (transaction.type === "INCENTIVE_REDEMPTION") {
      return (
        <Row className="shrink-0 items-center gap-1">
          <Text className="font-mono text-xs font-black text-violet-700">
            −1
          </Text>
          <StafferPetIncentiveToken sizePx={16} />
        </Row>
      );
    }

    return (
      <Text
        className={cn(
          "shrink-0 font-mono text-xs font-black",
          isIncome ? "text-emerald-700" : "text-orange-600",
        )}
      >
        {sign}
        {Math.abs(transaction.amount).toLocaleString("en-US")}
      </Text>
    );
  };

  const render = () => {
    return (
      <Row className="items-center justify-between gap-3 border-2 border-slate-900 bg-[#fffaf0] px-3 py-2 shadow-[2px_2px_0_#172033]">
        <Column className="min-w-0 gap-0">
          <Text className="truncate font-mono text-[10px] font-black uppercase tracking-[0.06em] text-slate-900">
            {transaction.description}
          </Text>
          <Text className="font-mono text-[8px] font-bold uppercase tracking-[0.08em] text-slate-500">
            {dates.formatShortDateTime(transaction.createdAt)}
          </Text>
        </Column>
        {renderAmount()}
      </Row>
    );
  };

  return render();
};
